/**
 * ---------------------------------------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * ---------------------------------------------------------------------------------------------
 */

import { Card, makeStyles, tokens, Image } from '@fluentui/react-components';
import React, { ReactNode } from 'react';

import { Flex, StyledText } from '@tdp/common/fluentV9MigrationComponents';

export interface IContentFeedCard {
  title: string;
  content: string;
  image?: string;
  link?: string;
  onClick?: () => void;
}

const useStyles = makeStyles({
  card: {
    cursor: 'pointer',
    padding: '20px',
    height: '240px',
    display: 'flex',
    gap: '24px',
    tabIndex: 0,
  },
  innerCard: {
    gap: "24px",
  },
  iconPlaceholder: {
    width: '64px',
    height: '64px',
    marginLeft: '12px',
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  inner: {
    gap: '4px',
  },
  tag: {
    color: tokens.colorNeutralForeground2,
  },
  title: {
    color: tokens.colorNeutralForeground1,
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '20px',
  },
  descr: {
    fontWeight: 400,
    lineHeight: '16px',
    paddingTop: '2px',
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  content: {
    color: tokens.colorNeutralForeground1,
    gap: '8px',
  },
  imageWrap: {
    paddingRight: '16px',
  },
  featuredWrap: {
    paddingLeft: '16px',
  },
  image: {
    borderRadius: '4px',
  },
  box: {
    width: '64px',
    height: '64px',
    borderRadius: '6px',
  },
  icon: {
    borderRadius: '6px',
    width: '64px',
    height: '64px',
  },
});

export const NewContentFeedCard: React.FC<IContentFeedCard> = ({
  title,
  content,
  image,
  onClick,
}) => {
  const classes = useStyles();

  const Body: ReactNode = (
    <Card className={classes.card} onClick={onClick} appearance="filled">
      <Flex column className={classes.innerCard}>
        <Flex column className={classes.iconPlaceholder}>
          <Image
            width="64px"
            height="64px"
            src={image}
            className={classes.icon}
            alt={'TBD'}
          />
        </Flex>
        <Flex column className={classes.content}>
          <StyledText className={classes.title} size="large">
            {title}
          </StyledText>
          <StyledText className={classes.descr} size="medium">
            {content}
          </StyledText>
        </Flex>
      </Flex>
    </Card>
  );

  return (
    <Flex column fill>
      {Body}
    </Flex>
  );
};
